import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useFirestore } from '../../hooks/useFirestore';
import { Pizzeria } from '../../types';
import PizzeriaForm from '../../components/pizzeria/PizzeriaForm';
import PizzeriaMenuManager from '../../components/pizzeria/PizzeriaMenuManager';
import SliceBox from '../../components/pizzeria/SliceBox';
import AdminLayout from '../../components/admin/AdminLayout';
import { PizzeriaWizard } from '../../components/pizzeria/PizzeriaWizard';

export default function PizzeriaManagement() {
  const { user } = useAuth();
  const { searchPizzerias } = useFirestore();
  const [pizzerias, setPizzerias] = useState<Pizzeria[]>([]);
  const [selectedPizzeria, setSelectedPizzeria] = useState<Pizzeria | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isWizardVisible, setWizardVisible] = useState(false);

  useEffect(() => {
    if (user) {
      console.log('🔄 PizzeriaManagement mounted');
      loadPizzerias();
    }
  }, [user, refreshTrigger]);

  const loadPizzerias = async () => {
    if (!user) {
      console.log('⛔ No user found in loadPizzerias');
      return;
    }
    
    console.log('👤 Loading pizzerie for user:', user.uid);
    setLoading(true);
    setError(null);
    
    try {
      const results = await searchPizzerias({ type: 'owner', query: user.uid });
      console.log('📊 Pizzerie trovate:', results.length);
      setPizzerias(results);
      
      if (results.length === 0) {
        console.log('🎯 Nessuna pizzeria trovata, mostro il wizard');
        setWizardVisible(true);
      } else {
        console.log('🏪 Pizzerie esistenti, nascondo il wizard');
        setWizardVisible(false);
      }
      
    } catch (err) {
      console.error('❌ Error loading pizzerie:', err);
      setError('Errore nel caricamento delle pizzerie');
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingClose = () => {
    console.log('👋 Onboarding closed');
    setWizardVisible(false);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleAddPizzeria = () => {
    setSelectedPizzeria(null);
    setIsFormOpen(true);
  };

  const handleEditPizzeria = (pizzeria: Pizzeria) => {
    setSelectedPizzeria(pizzeria);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedPizzeria(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSliceOperation = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleShowWizard = () => {
    setWizardVisible(true);
  };

  return (
    <AdminLayout title="Gestione Pizzerie" error={error}>
      <div className="space-y-8">
        <SliceBox onSliceOperation={handleSliceOperation} />

        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Le tue pizzerie</h2>
            <div className="space-x-4">
              <button
                onClick={handleShowWizard}
                className="text-gray-600 hover:text-gray-800 underline"
              >
                Procedura Guidata
              </button>
              <button
                onClick={handleAddPizzeria}
                className="bg-pizzaveg hover:bg-pizzaveg-dark text-white px-4 py-2 rounded-lg transition-colors"
              >
                Aggiungi Pizzeria
              </button>
            </div>
          </div>

          {loading ? (
            <div>Caricamento...</div>
          ) : pizzerias.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Non hai ancora registrato nessuna pizzeria.</p>
              <button
                onClick={handleAddPizzeria}
                className="mt-4 text-pizzaveg hover:text-pizzaveg-dark"
              >
                Registra la tua prima pizzeria
              </button>
            </div>
          ) : (
            <div className="grid gap-6">
              {pizzerias.map(pizzeria => (
                <div key={pizzeria.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-semibold">{pizzeria.name}</h3>
                        <p className="text-gray-600">{pizzeria.address}, {pizzeria.city}</p>
                      </div>
                      <button
                        onClick={() => handleEditPizzeria(pizzeria)}
                        className="text-pizzaveg hover:text-pizzaveg-dark"
                      >
                        Modifica dati
                      </button>
                    </div>
                    
                    <div className="mt-6">
                      <PizzeriaMenuManager 
                        pizzeria={pizzeria} 
                        onUpdate={loadPizzerias}
                        onSliceOperation={handleSliceOperation}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isFormOpen && (
        <PizzeriaForm
          pizzeria={selectedPizzeria}
          onClose={handleFormClose}
          userId={user?.uid}
        />
      )}

      <PizzeriaWizard 
        isVisible={isWizardVisible}
        onClose={handleOnboardingClose}
      />
    </AdminLayout>
  );
}